import {
  Paper,
  Modal,
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import styles from "./../../styles/charts/chartStyle.module.css";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  getChartBottomIcons,
  getChartInfoIcons,
} from "./twitter/TwitterWordCloud";

import {
  useAllChallengeNames,
  useAllFundNames,
  useAllProposersData,
} from "../../lib/hooks";
import { underscoreToCap } from "../../lib/util";
import { proposerFormType } from "../../lib/proposerAPI";
import { useNavigate } from "react-router-dom";
import ScatterPlotChart from "./protoCharts/ScatterPlotChart";
import { desc } from "../../lib/chartDescription";
const ProposerScatterPlot = () => {
  const [selectedX, setSelectedX] = useState<string>("average_rating");
  const [selectedY, setSelectedY] = useState<string>("total_result_votes");
  const [fund_id, setFund_id] = useState<any[]>([]);
  const [challenge_id, setChallenge_id] = useState<any[]>([]);
  const [formData, setFormData] = useState<proposerFormType>({
    num_per_page: 99999999999,
    page_number: 1,
    fund_id: "All",
    challenge_id: "All",
  });

  useEffect(() => {
    setFormData((prevState: any) => ({
      ...prevState,
      fund_id: fund_id.length === 0 ? "All" : fund_id.map((item) => item.id),
      challenge_id:
        challenge_id.length === 0 ? "All" : challenge_id.map((item) => item.id),
    }));
  }, [fund_id, challenge_id]);

  const {
    allChallengeNames,
    loading: allChallengeNameLoading,
    error: allChallengeNameError,
  } = useAllChallengeNames();

  let {
    loading: fundNameLoading,
    allFundNames,
    error: fundNamError,
  } = useAllFundNames();

  let challenge_dropDownOption =
    !fundNameLoading &&
    !allChallengeNameLoading &&
    allChallengeNameError === null &&
    fundNamError === null
      ? fund_id.length > 0
        ? allChallengeNames
            .filter((item) =>
              fund_id.map((item) => item.id).includes(item.fund_id)
            )
            .map((item) => {
              return {
                label: `${item.fund_name}:${item.challenge_name}`,
                id: item.challenge_id,
              };
            })
        : allChallengeNames.map((item) => {
            return {
              label: `${item.fund_name}:${item.challenge_name}`,
              id: item.challenge_id,
            };
          })
      : [];

  useEffect(() => {
    if (challenge_id.length > 0) {
      let newArr = challenge_id.filter((challenge) =>
        fund_id
          .map((item) => item.id)
          .includes(
            allChallengeNames.filter(
              (allChallenge) => allChallenge.challenge_id === challenge.id
            )[0]?.fund_id
          )
      );
      setChallenge_id(newArr);
    }
    //eslint-disable-next-line
  }, [fund_id.length]);

  // get proposers data
  let data: any[] = [];

  const { loading, proposerData, error } = useAllProposersData(formData);
  if (!loading && !error) {
    proposerData.items.forEach((proposer: any) => {
      data.push({
        x: proposer.quick_stats_data[selectedX],
        y: proposer.quick_stats_data[selectedY],
        z: proposer.user_id,
      });
    });
  }

  function genTitle() {
    return "Proposer Scatter Plot";
  }

  let navigate = useNavigate();
  const routeChange = (path: string) => {
    navigate(path);
  };

  function jumpToProposal(proposalID: number) {
    routeChange(`/proposer/${proposalID}`);
  }

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const names = [
    "total_result_votes",
    "total_up_votes",
    "total_down_votes",
    "number_of_proposals",
    "average_rating",
    "amount_requested",
    "funded_amount",
    "funded_proposals",
    "highest_proposal_rating",
    "highest_amount_requested",
    "conversion_percentage",
    "funding_percentage",
  ];

  const errorHandler = () => {
    setFund_id([]);
    setFormData({
      num_per_page: 99999999999,
      page_number: 1,
      fund_id: "All",
      challenge_id: "All",
    });
  };

  // let fundID = fund_id.length === 0 ? ["All"] : fund_id;

  const content = (showMaxButton: boolean) => {
    return (
      <Paper elevation={3} className={styles.chartContainer}>
        <div className="horizontalCenter">
          <Typography className={styles.chartHeader}>{genTitle()}</Typography>
        </div>
        <div className="horizontalCenter">
          <div className={styles.flexibleControlRow}>
            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={
                  !fundNameLoading && fundNamError === null
                    ? allFundNames.map((item) => {
                        return {
                          label: item.fund_name,
                          id: item.fund_id,
                        };
                      })
                    : []
                }
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setFund_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={fund_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Funds" />
                )}
              />
            </FormControl>

            <FormControl
              className={styles.flexibleformControlFullWidth}
              size="small"
            >
              <Autocomplete
                multiple={true}
                id="combo-box-demo"
                options={challenge_dropDownOption}
                size={"small"}
                onChange={(event: any, newValue: any[] | null) => {
                  setChallenge_id(newValue as any[]);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // disableClearable
                value={challenge_id}
                renderInput={(params) => (
                  <TextField {...params} label="All Challenges" />
                )}
              />
            </FormControl>
          </div>
        </div>

        <div className={styles.flexibleControlRow}>
          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              X-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedX}
              label={"X-axis Property"}
              onChange={(event) =>
                event.target.value !== selectedY
                  ? setSelectedX(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="average_rating"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.flexibleformControl} size="small">
            <InputLabel className={styles.flexSelectFont}>
              Y-axis Property
            </InputLabel>
            <Select
              className={styles.flexSelectFont}
              value={selectedY}
              label={"Y-axis Property"}
              onChange={(event) =>
                event.target.value !== selectedX
                  ? setSelectedY(event.target.value)
                  : alert("X and Y property cannot the same")
              }
              defaultValue="total_result_votes"
            >
              {names.map((c: any, i: any) => (
                <MenuItem value={c} key={i}>
                  {underscoreToCap(c)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {(!error || loading) && (
          <ScatterPlotChart
            xLabel={underscoreToCap(selectedX)}
            yLabel={underscoreToCap(selectedY)}
            chartData={data}
            jumpToProposal={(proposalID: number) => jumpToProposal(proposalID)}
            numberOfCategories={1}
            type="scatter"
          />
        )}

        {!loading && error && (
          <div className="horizontalCenter">
            <Paper elevation={4} className={styles.chartErrorBox}>
              <Typography>
                No data for your selection, please try again.
              </Typography>
              <Button variant="contained" onClick={errorHandler}>
                Try again
              </Button>
            </Paper>
          </div>
        )}

        {getChartBottomIcons(showMaxButton, setOpen)}
        {getChartInfoIcons(desc["proposer"]["scatter"])}
      </Paper>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className={styles.fullScreenChart}>{content(false)}</Box>
      </Modal>
      {content(true)}
    </>
  );
};

export default ProposerScatterPlot;
